import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Trash2, Plus, Radio, Check, Loader2, Camera } from "lucide-react";
import { api, imgUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import { toast } from "sonner";
import SellerFlowSteps from "@/components/SellerFlowSteps";

function listingPayload(p, extra = {}) {
  return {
    title: p.title,
    description: p.description || "",
    price: p.price,
    category: p.category,
    fabric: p.fabric || "",
    occasion: p.occasion || "",
    style: p.style || "",
    color: p.color || "",
    size: p.size || "M",
    tags: p.tags || [],
    images: p.images || [],
    brand: p.brand || "",
    is_live: extra.is_live ?? p.is_live,
    currency: p.currency || "USD",
    origin_country: p.origin_country || "US",
    ships_to: p.ships_to || ["*"],
    quantity: extra.quantity ?? p.quantity ?? 1,
    sku: extra.sku ?? p.sku ?? "",
    asin: extra.asin ?? p.asin ?? "",
    gtin: extra.gtin ?? p.gtin ?? "",
    upc: extra.upc ?? p.upc ?? "",
    ean: extra.ean ?? p.ean ?? "",
    fsn: extra.fsn ?? p.fsn ?? "",
    shipping_domestic: extra.shipping_domestic ?? p.shipping_domestic ?? 0,
    shipping_international: extra.shipping_international ?? p.shipping_international ?? 0,
  };
}

function ListingRow({ p, formatMoney, onToggle, onRemove, onSave }) {
  const [qty, setQty] = useState(p.quantity ?? 1);
  const [dom, setDom] = useState(p.shipping_domestic ?? 0);
  const [intl, setIntl] = useState(p.shipping_international ?? 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setQty(p.quantity ?? 1);
    setDom(p.shipping_domestic ?? 0);
    setIntl(p.shipping_international ?? 0);
  }, [p.quantity, p.shipping_domestic, p.shipping_international]);

  const nextQty = Math.max(0, Number(qty) || 0);
  const nextDom = Math.max(0, Number(dom) || 0);
  const nextIntl = Math.max(0, Number(intl) || 0);
  const dirty =
    nextQty !== Number(p.quantity ?? 1) ||
    nextDom !== Number(p.shipping_domestic ?? 0) ||
    nextIntl !== Number(p.shipping_international ?? 0);

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await onSave(p, {
        quantity: nextQty,
        shipping_domestic: nextDom,
        shipping_international: nextIntl,
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center gap-4">
        <Link to={`/product/${p.id}`} className="w-16 h-20 bg-aura-sand overflow-hidden rounded shrink-0">
          <img src={imgUrl(p.images?.[0])} alt={p.title} className="w-full h-full object-cover" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{p.title}</div>
          <div className="text-xs text-aura-muted mt-0.5 font-mono">
            {p.category} · {p.size} · {p.currency || "USD"}
            {(p.quantity ?? 1) <= 0 ? " · sold out" : ` · ${p.quantity ?? 1} in stock`}
          </div>
        </div>
        <div className="font-mono text-sm w-24 text-right">{formatMoney(p.price, p.currency || "USD", { convert: false })}</div>
        <button
          onClick={() => onToggle(p)}
          data-testid={`toggle-live-${p.id}`}
          className={`text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-colors ${
            p.is_live ? "bg-aura-accent text-white" : "border border-aura-border text-aura-muted hover:border-aura-accent"
          }`}
        >
          <Radio className="w-3 h-3" /> {p.is_live ? "Live" : "Publish"}
        </button>
        <Link
          to={`/seller/photoshoot?listing=${p.id}`}
          data-testid={`studio-${p.id}`}
          className="text-xs px-3 py-1.5 rounded-full border border-aura-border text-aura-muted hover:border-aura-accent flex items-center gap-1.5"
        >
          <Camera className="w-3 h-3" /> Studio
        </Link>
        <button onClick={() => onRemove(p.id)} data-testid={`delete-${p.id}`} className="text-aura-muted hover:text-aura-accent transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-3 pl-20 flex flex-wrap items-end gap-3">
        <label className="text-[10px] tracking-luxe uppercase text-aura-muted w-24">
          Qty available
          <input
            type="number"
            min="0"
            value={qty}
            data-testid={`qty-${p.id}`}
            onChange={(e) => setQty(e.target.value)}
            className="mt-1 w-full px-2 py-1.5 border border-aura-border outline-none text-sm font-mono rounded bg-aura-surface"
          />
        </label>
        <label className="text-[10px] tracking-luxe uppercase text-aura-muted w-36">
          Domestic shipping fee
          <input
            type="number"
            min="0"
            step="0.01"
            value={dom}
            data-testid={`ship-dom-${p.id}`}
            onChange={(e) => setDom(e.target.value)}
            className="mt-1 w-full px-2 py-1.5 border border-aura-border outline-none text-sm font-mono rounded bg-aura-surface"
          />
        </label>
        <label className="text-[10px] tracking-luxe uppercase text-aura-muted w-40">
          International shipping fee
          <input
            type="number"
            min="0"
            step="0.01"
            value={intl}
            data-testid={`ship-intl-${p.id}`}
            onChange={(e) => setIntl(e.target.value)}
            className="mt-1 w-full px-2 py-1.5 border border-aura-border outline-none text-sm font-mono rounded bg-aura-surface"
          />
        </label>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid={`save-inventory-${p.id}`}
          className="px-4 py-1.5 bg-aura-accent text-white text-xs font-medium rounded-lg flex items-center gap-1.5 disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Save
        </button>
      </div>
    </div>
  );
}

export default function MyProducts() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { formatMoney } = useLocale();

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["my-products", user?.id],
    queryFn: async () => (await api.get(`/products?seller_id=${user.id}&limit=100`)).data,
  });

  const units = products.reduce((s, p) => s + (Number(p.quantity) || 0), 0);

  const remove = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      qc.invalidateQueries({ queryKey: ["my-products"] });
      toast.success("Product removed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleLive = async (p) => {
    try {
      await api.put(`/products/${p.id}`, listingPayload(p, { is_live: !p.is_live }));
      qc.invalidateQueries({ queryKey: ["my-products"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveListing = async (p, extra) => {
    const { data } = await api.put(`/products/${p.id}/inventory`, {
      quantity: extra.quantity,
      shipping_domestic: extra.shipping_domestic,
      shipping_international: extra.shipping_international,
    });
    qc.setQueryData(["my-products", user?.id], (rows) =>
      (rows || []).map((row) => (row.id === p.id ? { ...row, ...data } : row))
    );
    toast.success("Inventory saved");
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-28 pb-24">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-2">Inventory</div>
          <h1 className="text-3xl font-semibold">Your products</h1>
          {products.length > 0 && (
            <p className="text-sm text-aura-muted mt-2">{products.length} listings · {units} units in stock · drafts stay hidden until you publish</p>
          )}
          <div className="mt-6">
            <SellerFlowSteps current={3} />
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/seller/crosslist" className="px-5 py-2.5 border border-aura-border text-sm rounded-lg">Crosslist</Link>
          <Link to="/seller/add" className="px-5 py-2.5 bg-aura-accent text-white text-sm flex items-center gap-2 rounded-lg" data-testid="add-product-link">
            <Plus className="w-4 h-4" /> New listing
          </Link>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 shimmer rounded-lg" />)}</div>
      ) : products.length === 0 ? (
        <div className="py-20 text-center">
          <p className="text-aura-muted mb-6">No products yet. Onboard your first piece with AI.</p>
          <Link to="/seller/add" className="px-8 py-3 bg-aura-accent text-white text-sm inline-block rounded-lg">Add product</Link>
        </div>
      ) : (
        <div className="bg-aura-surface border border-aura-border rounded-lg divide-y divide-aura-border" data-testid="my-products-list">
          {products.map((p) => (
            <ListingRow
              key={p.id}
              p={p}
              formatMoney={formatMoney}
              onToggle={toggleLive}
              onRemove={remove}
              onSave={saveListing}
            />
          ))}
        </div>
      )}
    </div>
  );
}
